/**
 * Place一覧の 1 行
 *
 * 仮想リストで大量に描画するため memo 化し、選択状態などが変わった行だけ再描画する。
 */

import { format } from 'date-fns'
import { ja } from 'date-fns/locale'
import { Camera, ExternalLink, MapPin, Pencil, Trash2 } from 'lucide-react'
import { memo } from 'react'

import type { Place } from '@/api/types/overland'
import { AuthImage } from '@/components/ui/auth-image'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { stripQueryParams } from '@/lib/utils'

export type PlaceListRowHandlers = {
  onToggleSelect: (placeId: number) => void
  onClick: (place: Place) => void
  onReGeocode: (place: Place) => void
  onEditMyPlace: (place: Place) => void
  onDelete: (place: Place) => void
}

type PlaceListRowProps = PlaceListRowHandlers & {
  place: Place
  isSelectMode: boolean
  isSelected: boolean
}

// 画像URLを取得
const getImageUrl = (place: Place): string | null => {
  if (place.images && place.images.length > 0) {
    return stripQueryParams(place.images[0].url)
  }
  return null
}

export const PlaceListRow = memo(function PlaceListRow({
  place,
  isSelectMode,
  isSelected,
  onToggleSelect,
  onClick,
  onReGeocode,
  onEditMyPlace,
  onDelete,
}: PlaceListRowProps) {
  const imageUrl = getImageUrl(place)

  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer ${isSelectMode && isSelected ? 'ring-2 ring-primary' : ''}`}
      onClick={isSelectMode ? () => onToggleSelect(place.id) : () => onClick(place)}
    >
      {/* 選択モード時のチェックボックス */}
      {isSelectMode && (
        <Checkbox checked={isSelected} onCheckedChange={() => onToggleSelect(place.id)} className="shrink-0" />
      )}

      {/* 画像 */}
      {imageUrl ? (
        <AuthImage src={imageUrl} alt={place.display_name} className="size-12 rounded-lg object-cover shrink-0" />
      ) : (
        <div className="size-12 rounded-lg bg-muted flex items-center justify-center shrink-0">
          <MapPin className="size-5 text-muted-foreground" />
        </div>
      )}

      {/* 情報 */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {!isSelectMode ? (
            <button
              type="button"
              className="flex items-center gap-1 min-w-0 text-left underline decoration-muted-foreground/50 underline-offset-2 hover:decoration-primary"
              onClick={(e) => {
                e.stopPropagation()
                onReGeocode(place)
              }}
            >
              <h3 className="font-medium text-sm truncate">{place.display_name}</h3>
              <Pencil className="size-3.5 text-muted-foreground shrink-0" />
            </button>
          ) : (
            <h3 className="font-medium text-sm truncate">{place.display_name}</h3>
          )}
          {place.google_maps_url && (
            <a
              href={place.google_maps_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-600"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="size-3" />
            </a>
          )}
        </div>

        {place.address && <p className="text-xs text-muted-foreground truncate">{place.address}</p>}

        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <Badge variant="secondary" className="text-xs">
            {place.visit_count}回訪問
          </Badge>
          {place.last_visited_at && (
            <span className="text-xs text-muted-foreground">
              最終: {format(new Date(place.last_visited_at), 'M/d', { locale: ja })}
            </span>
          )}
          {place.photo_count !== undefined && place.photo_count > 0 && (
            <Badge variant="outline" className="text-xs border-sky-500/50 text-sky-600 dark:text-sky-400 gap-1">
              <Camera className="size-3" />
              {place.photo_count}
            </Badge>
          )}
          {place.categories.slice(0, 2).map((cat) => (
            <Badge key={cat.name} variant="outline" className="text-xs">
              {cat.display_name ?? cat.name}
            </Badge>
          ))}
          {place.provider === 'yahoo' && (
            <Badge variant="outline" className="text-xs border-amber-500/50 text-amber-600 dark:text-amber-400">
              Yahoo
            </Badge>
          )}
          {place.is_custom && (
            <Badge variant="outline" className="text-xs border-blue-500/50 text-blue-600 dark:text-blue-400">
              マイプレイス
            </Badge>
          )}
        </div>
      </div>

      {/* アクションボタン（選択モードでない時のみ） */}
      {!isSelectMode && (
        <div className="flex items-center gap-1 shrink-0">
          {place.is_custom && (
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-blue-500"
              onClick={(e) => {
                e.stopPropagation()
                onEditMyPlace(place)
              }}
            >
              <Pencil className="size-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation()
              onDelete(place)
            }}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      )}
    </div>
  )
})
